import * as React from 'react';
import API, { GenericAddResponse } from "../../common/API";
import { useAuth } from "react-oidc-context";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QuestionType, QuizQuestion } from '../../common/TrainingTypes';
import { useTranslation } from 'react-i18next';

interface StartQuizResponse {
	id: number,
    questions: Question[]
}

interface Question {
	id: number,
    question: string,
    type: QuestionType
	options: string[],
    selected: number | undefined,
    answer: number | undefined,
    content: string | undefined
}

interface CompleteLessonRequest {
	answers: QuizAnswer[]
}

interface QuizAnswer {
	id: number,
    choice: number,
    content: string | undefined
}

interface CompleteLessonResponse {
	grade: number;
	passed: boolean;
	answers: { [questionId: number]: number }
}

export function QuizComponent(props: { userLessonResultId: number | null, questions: QuizQuestion[] | null, completeQuiz: (grade: number) => void }) {
    const auth = useAuth();
    const { t } = useTranslation();
    const variations: number[][] = [[2, 0, 1, 3], [3, 1, 0, 2], [0, 1, 3, 2], [3, 2, 1, 0], [0, 1, 2, 3]]

    const [questions, setQuestions] = React.useState<Question[]>()
    const [complete, setComplete] = React.useState<boolean>()
    const [passed, setPassed] = React.useState<boolean | null>(null)
    const [grade, setGrade] = React.useState<number|null>()

	const setAnswer = (questionId: number, choice: number, content: string|undefined) => {
		const question = questions?.find((e) => e.id === questionId);
		if (question === undefined)
			return;
        question.selected = choice;
        question.content = content;

		// Set complete if all questions have selection
		setComplete(!questions?.some((e) => e.selected === undefined && e.content === undefined))
	}

	const completeQuiz = async () => {
		if (questions == null)
			return;

		const req: CompleteLessonRequest = {
            answers: questions.map((e): QuizAnswer => {
                return { id: e.id, choice: e.selected ?? 0, content: e.content }
			})
		}

        if (props.userLessonResultId != null) {
            const res: CompleteLessonResponse | null = await API.post("/api/v1/training/quiz/" + props.userLessonResultId, req, auth);
            if (res == null)
                return;

            questions.forEach((e) => {
                const q = res.answers[e.id]
                if (q == null)
                    return;
                e.answer = q
            });

            setPassed(res.passed)
            setGrade(res.grade)
        } else {

            questions.forEach((question) => {
                const answer = props.questions?.find((e) => e.id === question.id);
                if (answer == null)
                    return;

                var correct = 0;

                switch (question.type) {
                    case QuestionType.MultipleChoice:
                        if (answer.key == null)
                            return;
                        question.answer = variations[answer.key].indexOf(0);
                        if (question.selected === question.answer)
                            correct++;
                        break;
                    case QuestionType.TrueFalse:
                        if ((answer.answer === 'true' && question.selected === 1) ||
                            (answer.answer === 'false' && question.selected === 0)) {
                            correct++;
                            question.answer = answer.answer === 'true' ? 1 : 0;
                        }
                        break;
                }

                setGrade(Math.round(correct / questions.filter((e) => e.type !== QuestionType.Textarea).length) * 100);
                setPassed(true);
            });
        }        
    }

    const retakeQuiz = async () => {
        const response: StartQuizResponse | null = await API.get("/api/v1/training/quiz/" + props.userLessonResultId, auth);
        if (response == null) {
            return;
        }
        setQuestions([]);
        setComplete(false)
        setPassed(null)
        setGrade(null)
        setQuestions(response.questions);
    }

    const leaveQuiz = async () => {
        if (grade == null)
            return;

        props.completeQuiz(grade);
    }

	React.useEffect(() => {
		const getQuizQuestions = async () => {
			const response: StartQuizResponse | null = await API.get("/api/v1/training/quiz/" + props.userLessonResultId, auth);
			if (response == null) {
				return;
			}
			setQuestions(response.questions);
        }

        setPassed(null);
        setComplete(false);
        setGrade(null);

        if (props.userLessonResultId != null)
            getQuizQuestions();

        if (props.questions != null) {
            var questions: Question[] = []
            props.questions.forEach((e) => {

                // Randomize the questions for multiple choice
                var opts: string[] = [];
                if (e.type === QuestionType.MultipleChoice) {
                    e.key = Math.floor(Math.random() * 4);
                    variations[e.key].forEach((n) => {
                        switch (n) {
                            case 0:
                                opts.push(e.answer);
                                break;
                            case 1:
                                if (e.wrongAnswer1 !== null)
                                    opts.push(e.wrongAnswer1);
                                break;
                            case 2:
                                if (e.wrongAnswer2 !== null)
                                    opts.push(e.wrongAnswer2);
                                break;
                            case 3:
                                if (e.wrongAnswer3 !== null)
                                    opts.push(e.wrongAnswer3);
                                break;
                        }
                    })
                }

                questions.push({
                    id: e.id,
                    question: e.question,
                    type: e.type,
                    options: opts,
                    answer: undefined,
                    content: undefined,
                    selected: undefined
                });
            });
            setQuestions(questions);
        }
	}, [auth, props.userLessonResultId, props.questions])

    return (
        <>
		    <div className="card">
                <div className="card-header">
                    <h5 className="card-title mb-0">{props.userLessonResultId != null ? t('training:lessonQuiz') : t('training:review')}</h5>
				</div>
				<div className="card-body">
					<ol>
						{questions?.map((e) => {
							return (
								<QuizQuestionComponent key={e.id} question={e} setAnswer={setAnswer} />
							)
						})}
					</ol>
				</div>
                <div className="card-footer">
                    {passed !== null ?
                        <div className={"alert alert-outline-coloured " + (passed ? "alert-success" : "alert-danger")}>
                            <div className="alert-icon"><FontAwesomeIcon icon={(passed ? "check" : "times") }></FontAwesomeIcon></div>
                            <div className="alert-message">
                                <strong className="me-2">{questions?.filter((e) => e.answer === e.selected).length} / {questions?.filter((e) => e.type !== QuestionType.Textarea).length}</strong>
                                {passed ? <strong>{t('training:passed')}</strong> : <strong>{t('training:failed')}</strong>} &nbsp; &bull; &nbsp;
                                {t('training:reviewAnswers')}
                            </div>
                        </div> : <></>}
				</div>
            </div>


            {passed != null && passed ? <button type="button" className="btn btn-primary w-100" onClick={leaveQuiz}>{t('common:next')}</button> :
                passed != null && !passed ? <button type="button" className="btn btn-primary w-100" onClick={retakeQuiz}>{t('training:retry')}</button> :
                    passed == null ? <button type="button" className="btn btn-primary w-100" onClick={completeQuiz} disabled={!complete}>{t('training:complete')}</button> :
                        <></>}
        </>
	)
}

function QuizQuestionComponent(props: { question: Question, setAnswer: (questionId: number, index: number, contents: string | undefined) => void }) {
    const { t } = useTranslation();

	return (
        <li>
            <strong>{props.question.question}</strong>
            {props.question.answer !== undefined ?
                <FontAwesomeIcon className={"ms-2 " + (props.question.answer === props.question.selected ? "text-success" : "text-danger")}
                    icon={(props.question.answer === props.question.selected ? "check" : "times")}></FontAwesomeIcon>
                :<></>}

            {props.question.type === QuestionType.MultipleChoice ?
                <ul className="list-unstyled">
                    {props.question?.options.map((e, index) => {
                        return (
                            <li key={index}>
                                <label className="form-check">
                                    <input className="form-check-input" type="radio" name={"question-" + props.question.id} id={"question-choice-" + index} onChange={(e) => props.setAnswer(props.question.id, index, undefined)} />
                                    <span className={"form-check-label " + (props.question.answer !== undefined && props.question.answer === index ? "text-success" :
                                        props.question.answer !== undefined && props.question.answer !== index ? "text-danger text-decoration-line-through" : "")}>{e}</span>
                                </label>
                            </li>
                        )
                    })}
                </ul> : <></>}
            {props.question.type === QuestionType.TrueFalse ?
                <ul className="list-unstyled">
                    <li>
                        <label className="form-check">
                            <input className="form-check-input" type="radio" name={"question-" + props.question.id} id={"question-choice-true"} onChange={(e) => props.setAnswer(props.question.id, 1, undefined)} />
                            <span className={"form-check-label " + (props.question.answer !== undefined && props.question.answer === 1 ? "text-success" :
                                props.question.answer !== undefined && props.question.answer !== 1 ? "text-danger text-decoration-line-through" : "")}>{t('common:yes')}</span>
                        </label>
                        <label className="form-check">
                            <input className="form-check-input" type="radio" name={"question-" + props.question.id} id={"question-choice-false"} onChange={(e) => props.setAnswer(props.question.id, 0, undefined)} />
                            <span className={"form-check-label " + (props.question.answer !== undefined && props.question.answer === 0 ? "text-success" :
                                props.question.answer !== undefined && props.question.answer !== 0 ? "text-danger text-decoration-line-through" : "")}>{t('common:no')}</span>
                        </label>
                    </li>
                </ul>
                : <></>}
            {props.question.type === QuestionType.Textarea ?
                <div className="col-10">
                    <textarea className="form-control" name={"question-" + props.question.id} onChange={(e) => props.setAnswer(props.question.id, 0, e.target.value)} />
                </div>
                : <></>}
		</li>
	)
}